import { Card } from "@/components/ui/card";
import {
  TrendingUp,
  CheckCircle2,
  UserPlus,
  Coins,
  AlertTriangle,
} from "lucide-react";
import { isSameDay, isPast, startOfDay } from "date-fns";
import { useCurrency } from "@/hooks/useUserCurrency";

interface DailyReportProps {
  loans: Array<{
    id: string;
    clientId: string;
    amount: number;
    loanDate: Date;
    dueDate: Date;
    status: "paid" | "active" | "overdue";
    updatedAt?: Date;
  }>;
  payments: Array<{
    id: string;
    loanId: string;
    amount: number;
    interestAmount: number;
    paidAt: Date;
  }>;
  clients: Array<{ id: string; createdAt: Date }>;
}

export function DailyReport({ loans, payments, clients }: DailyReportProps) {
  const { format: fmtBRL } = useCurrency();
  const today = startOfDay(new Date());

  // 1. Total emprestado hoje
  const loansToday = loans.filter((l) => isSameDay(l.loanDate, today));
  const totalLoanedToday = loansToday.reduce((s, l) => s + l.amount, 0);

  // 2. Dívidas liquidadas hoje (loans que viraram paid hoje)
  // Aproximação: pagamentos de hoje cujo loan está com status paid
  const paidLoansSet = new Set(loans.filter((l) => l.status === "paid").map((l) => l.id));
  const settledPaymentsToday = payments.filter(
    (p) => isSameDay(p.paidAt, today) && paidLoansSet.has(p.loanId)
  );
  const settledClientsToday = new Set(
    settledPaymentsToday
      .map((p) => loans.find((l) => l.id === p.loanId)?.clientId)
      .filter(Boolean) as string[]
  ).size;
  const settledAmountToday = settledPaymentsToday.reduce((s, p) => s + p.amount, 0);

  // 3. Clientes novos hoje
  const newClientsToday = clients.filter((c) => isSameDay(c.createdAt, today)).length;

  // 4. Juros recebidos hoje
  const interestToday = payments
    .filter((p) => isSameDay(p.paidAt, today))
    .reduce((s, p) => s + p.interestAmount, 0);

  // 5. Pagamentos em atraso (vencem hoje ou já vencidos)
  const overdueOrDueToday = loans.filter(
    (l) =>
      (l.status === "active" || l.status === "overdue") &&
      (isSameDay(l.dueDate, today) || isPast(l.dueDate))
  ).length;

  const items = [
    {
      icon: TrendingUp,
      label: "Total emprestado hoje",
      value: fmtBRL(totalLoanedToday),
      sub: `${loansToday.length} empréstimo${loansToday.length === 1 ? "" : "s"}`,
      tone: "primary",
    },
    {
      icon: CheckCircle2,
      label: "Dívidas liquidadas hoje",
      value: `${settledClientsToday}`,
      sub: fmtBRL(settledAmountToday),
      tone: "success",
    },
    {
      icon: UserPlus,
      label: "Clientes novos hoje",
      value: `${newClientsToday}`,
      sub: newClientsToday === 0 ? "Nenhum cadastro" : "Cadastrados hoje",
      tone: "primary",
    },
    {
      icon: Coins,
      label: "Juros recebidos hoje",
      value: fmtBRL(interestToday),
      sub: "Receita do dia",
      tone: "gold",
    },
    {
      icon: AlertTriangle,
      label: "Pagamentos em atraso",
      value: `${overdueOrDueToday}`,
      sub: "Vence hoje ou atrasado",
      tone: "destructive",
    },
  ] as const;

  const toneMap: Record<string, { icon: string; bg: string; bar: string; value: string }> = {
    primary: {
      icon: "text-primary",
      bg: "bg-primary/10 border-primary/20",
      bar: "from-primary via-primary/60 to-transparent",
      value: "text-foreground",
    },
    success: {
      icon: "text-success",
      bg: "bg-success/10 border-success/20",
      bar: "from-success via-success/60 to-transparent",
      value: "text-success",
    },
    gold: {
      icon: "text-gold",
      bg: "bg-gold/10 border-gold/20",
      bar: "from-gold via-gold/60 to-transparent",
      value: "text-gold",
    },
    destructive: {
      icon: "text-destructive",
      bg: "bg-destructive/10 border-destructive/20",
      bar: "from-destructive via-destructive/60 to-transparent",
      value: "text-destructive",
    },
  };

  return (
    <Card className="p-5 lg:p-6 glass">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg lg:text-xl font-display font-semibold tracking-tight">
            Relatório do Dia
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Resumo inteligente das atividades de hoje
          </p>
        </div>
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.2em]">
          {new Date().toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "short",
          })}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
        {items.map((it, i) => {
          const t = toneMap[it.tone];
          const Icon = it.icon;
          return (
            <div
              key={i}
              className="relative overflow-hidden rounded-2xl glass-subtle p-4 transition-all hover:scale-[1.01]"
            >
              <div
                className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${t.bar}`}
              />
              <div
                className={`inline-flex items-center justify-center h-9 w-9 rounded-xl border ${t.bg} mb-3`}
              >
                <Icon className={`h-4 w-4 ${t.icon}`} />
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                {it.label}
              </div>
              <div className={`mt-1 text-lg lg:text-xl font-semibold tabular ${t.value}`}>
                {it.value}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{it.sub}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
